import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Heart,
  Users,
  DollarSign,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  CheckCircle,
  Award,
  Shield,
  Zap,
  ExternalLink,
} from 'lucide-react';
import { title } from 'process';
import GoshalaDirectory from './pages/ghoshala/GoshalaDirectory';

// TypeScript interfaces
interface SectionProps {
  id: string;
  title: string;
  titleKannada?: string;
  children: React.ReactNode;
  bgColor?: string;
}

interface CardProps {
  title: string;
  titleKannada?: string;
  description: string;
  buttonText: string;
  onClick: () => void;
  image?: string;
  icon?: React.ReactNode;
  color?: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
  district: string;
  subscription: boolean;
}


interface ProcessStep { step:string,title:string, titleKannada:string, desc:string , icon:string }
interface TestimonialProps { name:string, role:string, quote:string, image:string }
interface FAQProps { question:string, answer:string }

// Process Section
const Process: React.FC = () => {
  const steps: ProcessStep[] = [
    {
      step: '01',
      title: 'Choose Amount',
      titleKannada: 'ದಾನ ಆಯ್ಕೆ',
      desc: 'Select donation amount based on your preference - from ₹70 daily feeding to ₹5000 medical support.',
      icon: '💰',
    },
    {
      step: '02',
      title: 'Fill Details',
      titleKannada: 'ವಿವರಗಳನ್ನು ಭರ್ತಿ ಮಾಡಿ',
      desc: 'Enter your name, email, and phone number for payment processing and certificate generation.',
      icon: '📝',
    },
    {
      step: '03',
      title: 'Secure Payment',
      titleKannada: 'ಸುರಕ್ಷಿತ ಪಾವತಿ',
      desc: 'Complete payment using Razorpay with UPI, cards, net banking, or wallet options.',
      icon: '💳',
    },
    {
      step: '04',
      title: 'Get Certificate',
      titleKannada: 'ಪ್ರಮಾಣಪತ್ರ ಪಡೆಯಿರಿ',
      desc: 'Receive your GOVU RAKSHATHI RAKSHITHA certificate instantly via PDF download.',
      icon: '📜',
    },
  ];

  return (
    <Section
      id="process"
      title="Simple Donation Process"
      titleKannada="ಸರಳ ದಾನ ಪ್ರಕ್ರಿಯೆ"
      bgColor="bg-white"
    >
      <p className="text-xl text-gray-600 text-center mb-12">Complete your donation in just 4 easy steps</p>
      
      <div className="max-w-4xl mx-auto">
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-16 left-1/2 transform -translate-x-1/2 w-full max-w-3xl">
            <div className="h-1 bg-gradient-to-r from-red-500 via-yellow-500 to-red-500 rounded-full opacity-30"></div>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                {/* Step Number Circle */}
                <div className="relative mb-6">
                  <div className="w-32 h-32 mx-auto bg-gradient-to-br from-red-500 to-yellow-500 rounded-full flex items-center justify-center text-white shadow-lg">
                    <div className="text-center">
                      <div className="text-4xl mb-2">{step.icon}</div>
                      <div className="text-sm font-bold">STEP {step.step}</div>
                    </div>
                  </div>
                  
                  {/* Connecting Arrow */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 left-full transform -translate-y-1/2 w-8 h-8 text-red-500">
                      <ArrowRight className="w-full h-full opacity-60" />
                    </div>
                  )}
                </div>
                
                {/* Step Content */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                  <h4 className="text-lg font-semibold text-red-600">{step.titleKannada}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Call to Action */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-red-50 to-yellow-50 rounded-2xl p-8 border-2 border-red-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Make a Difference?</h3>
            <p className="text-gray-600 mb-6">Join thousands of donors supporting cow welfare across Karnataka</p>
            <motion.button
              className="bg-gradient-to-r from-red-600 to-yellow-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.getElementById('services');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              Start Donation Process | ದಾನ ಪ್ರಕ್ರಿಯೆ ಪ್ರಾರಂಭಿಸಿ
            </motion.button>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

// Testimonials Component
const Testimonials: React.FC = () => {
  const testimonials: TestimonialProps[] = [
    {
      name: 'Priya Sharma',
      role: 'Donor, Bengaluru',
      quote: 'Donating through Punyakoti Dattu Yojana has been deeply fulfilling. The process is simple and I feel connected to Karnataka\'s heritage.',
      image: '👩‍💼',
    },
    {
      name: 'Ramesh Patil',
      role: 'Monthly Donor, Mysore',
      quote: 'The certificate I received is beautiful! Knowing my donation directly helps cows in goshalas gives me immense satisfaction.',
      image: '👨‍🌾',
    },
    {
      name: 'Anita Rao',
      role: 'Corporate Donor, Mangalore',
      quote: 'Our company has been supporting this initiative for months. The transparency and impact reports keep us motivated to continue.',
      image: '👩‍💻',
    },
    {
      name: 'Vikram Gowda',
      role: 'Regular Donor, Hassan',
      quote: 'From ₹70 daily feeding to medical support, every donation option is meaningful. This platform makes cow protection accessible to all.',
      image: '👨‍🎓',
    },
  ];

  return (
    <Section
      id="testimonials"
      title="What Our Donors Say"
      titleKannada="ನಮ್ಮ ದಾನಿಗಳು ಏನು ಹೇಳುತ್ತಾರೆ"
      bgColor="bg-gradient-to-br from-red-50 to-yellow-50"
    >
      <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
        Hear from individuals and organizations across Karnataka who have supported the Punyakoti Dattu Yojana.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={index}
            className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            {/* Avatar */}
            <div className="w-16 h-16 rounded-full mx-auto mb-4 bg-gradient-to-r from-red-100 to-yellow-100 flex items-center justify-center text-2xl border-2 border-red-200">
              {testimonial.image}
            </div>
            
            {/* Quote */}
            <div className="mb-4">
              <div className="text-red-500 text-4xl leading-none mb-2">"</div>
              <p className="text-gray-600 italic text-sm leading-relaxed">{testimonial.quote}</p>
            </div>
            
            {/* Author */}
            <div className="text-center">
              <h4 className="text-lg font-bold text-gray-900">{testimonial.name}</h4>
              <p className="text-red-600 text-sm font-medium">{testimonial.role}</p>
            </div>
            
            {/* Rating Stars */}
            <div className="flex justify-center mt-3 space-x-1">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="text-yellow-400 text-sm">⭐</span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Statistics */}
      <motion.div
        className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        {[
          { number: '5,000+', label: 'Happy Donors', icon: '😊' },
          { number: '98%', label: 'Satisfaction Rate', icon: '💯' },
          { number: '₹50L+', label: 'Total Donations', icon: '💰' },
          { number: '4.9/5', label: 'Average Rating', icon: '⭐' },
        ].map((stat, index) => (
          <div key={index} className="text-center p-6 bg-white rounded-xl shadow-lg">
            <div className="text-3xl mb-2">{stat.icon}</div>
            <div className="text-2xl font-bold text-red-600">{stat.number}</div>
            <div className="text-gray-600 text-sm">{stat.label}</div>
          </div>
        ))}
      </motion.div>
    </Section>
  );
};

// FAQ Component
const FAQ: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const faqs: FAQProps[] = [
    {
      question: 'What is the Punyakoti Dattu Yojana?',
      answer: 'It\'s a Karnataka Government initiative launched on July 28, 2022, to protect cows and sustain goshalas through donations and community support. The program supports 215 government-registered goshalas across Karnataka.',
    },
    {
      question: 'What is the minimum donation amount?',
      answer: 'You can donate as little as ₹10. However, we recommend ₹70 for daily cow feeding, ₹100 for basic support, ₹500 for weekly care, or ₹2,100 for monthly comprehensive care.',
    },
    
    {
      question: 'How do I receive my donation certificate?',
      answer: 'Your GOVU RAKSHATHI RAKSHITHA (Certificate of Thanksgiving) is automatically generated and can be downloaded as a PDF immediately after successful payment completion.',
    },
    {
      question: 'Can I donate for specific purposes?',
      answer: 'Yes, you can choose to donate for daily feeding (₹70), basic cow care (₹100), weekly support (₹500), monthly care (₹2,100), or medical emergencies (₹5,000). You can also make custom donations.',
    },
    {
      question: 'Is the payment process secure?',
      answer: 'Absolutely! We use Razorpay\'s secure payment gateway with bank-level encryption. You can pay via UPI, credit/debit cards, net banking, or digital wallets. All transactions are protected and verified.',
    },
    {
      question: 'Can I visit the goshalas after donating?',
      answer: 'Yes, donors are welcome to visit the goshalas. Contact the specific goshala directly to arrange visits and see the impact of your donation. Location details are available on the official portal.',
    },
    {
      question: 'How are the donated funds utilized?',
      answer: 'Funds directly support cow care including daily fodder, clean water, veterinary services, medical treatments, shelter maintenance, staff salaries, and infrastructure development at registered goshalas.',
    },
    {
      question: 'Can I set up monthly recurring donations?',
      answer: 'Currently, each donation is processed as a one-time payment. However, you can manually repeat donations monthly. We\'re working on implementing automatic recurring donation features.',
    },
    {
      question: 'What if my payment fails or gets stuck?',
      answer: 'If your payment fails, no amount will be deducted. If payment is stuck, contact our support at +91 80-2235-2828 or support@punyakoti.kar.gov.in with your transaction details for immediate assistance.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <Section
      id="faq"
      title="Frequently Asked Questions"
      titleKannada="ಪದೇ ಪದೇ ಕೇಳಲಾಗುವ ಪ್ರಶ್ನೆಗಳು"
      bgColor="bg-white"
    >
      <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
        Find answers to common questions about donations, certificates, and the Punyakoti Dattu Yojana initiative.
      </p>
      
      <div className="max-w-4xl mx-auto space-y-4">
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            className="bg-gradient-to-r from-red-50 to-yellow-50 rounded-xl border border-red-100 overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <button
              className="w-full p-6 text-left hover:bg-white/50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-inset"
              onClick={() => toggleFAQ(index)}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</h3>
                <div className={`text-red-500 transform transition-transform duration-200 ${openFAQ === index ? 'rotate-45' : ''}`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
              </div>
            </button>
            
            <motion.div
              initial={false}
              animate={{
                height: openFAQ === index ? 'auto' : 0,
                opacity: openFAQ === index ? 1 : 0,
              }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-6">
                <div className="border-t border-red-200 pt-4">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        ))}
      </div>
      
      {/* Contact Support */}
      <motion.div
        className="mt-12 text-center"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="bg-gradient-to-r from-red-600 to-yellow-500 text-white rounded-2xl p-8 max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
          <p className="mb-6 opacity-90">Our support team is here to help you make a meaningful donation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              className="bg-white text-red-600 px-6 py-3 rounded-xl font-semibold hover:bg-red-50 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.open('tel:+918022352828')}
            >
              📞 Call Support
            </motion.button>
            <motion.button
              className="bg-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/30 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              💬 Send Message
            </motion.button>
          </div>
        </div>
      </motion.div>
    </Section>
  );
};

interface GoshalaData {
  slNo: number;
  district: string;
  taluk: string;
  goshalaName: string;
  animalCount: number;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
  district: string;
  subscription: boolean;
}

interface PaymentProps {
  amount: number;
  donorName: string;
  email: string;
  phone: string;
  type: 'donation';
  donationPurpose?: string;
  selectedGoshala?: GoshalaData;
}



// Mock goshala data - in real implementation, this would come from the Excel file
const GOSHALA_DATA: GoshalaData[] = [
  { slNo: 1, district: "Kolara", taluk: "Bangarapet", goshalaName: "Dhathri Godhama", animalCount: 103 },
  { slNo: 2, district: "Udupi", taluk: "Karkal", goshalaName: "Ananthakrishna Goshala", animalCount: 93 },
  { slNo: 3, district: "Mysuru", taluk: "Nanjangud", goshalaName: "SRI SHIVARATHRESWARA RURAL DEVELOPMENT FOUNDATION", animalCount: 50 },
  { slNo: 4, district: "Shivamogga", taluk: "Shimoga", goshalaName: "SHIMOGA ZILLA BRAHMANA MAHASABHA (R) SURABHI GOSHALE", animalCount: 195 },
  { slNo: 5, district: "Bengaluru Urban", taluk: "Bengaluru", goshalaName: "Bengaluru Goshala Trust", animalCount: 150 },
  { slNo: 6, district: "Dakshina Kannada", taluk: "Mangalore", goshalaName: "Mangalore Cow Protection Society", animalCount: 120 },
  { slNo: 7, district: "Belagavi", taluk: "Belagavi", goshalaName: "Belagavi Regional Goshala", animalCount: 180 },
  { slNo: 8, district: "Kalaburgi", taluk: "Gulbarga", goshalaName: "Gulbarga Gau Seva Samiti", animalCount: 165 },
];

// Get unique districts from goshala data
const getUniqueDistricts = (): string[] => {
  return Array.from(new Set(GOSHALA_DATA.map(g => g.district))).sort();
};

// Get goshalas by district
const getGoshalasByDistrict = (district: string): GoshalaData[] => {
  return GOSHALA_DATA.filter(g => g.district === district);
};

// Get district statistics
const getDistrictStats = () => {
  const stats: { [key: string]: { count: number; totalAnimals: number } } = {};
  GOSHALA_DATA.forEach(g => {
    if (!stats[g.district]) {
      stats[g.district] = { count: 0, totalAnimals: 0 };
    }
    stats[g.district].count++;
    stats[g.district].totalAnimals += g.animalCount;
  });
  return stats;
};



declare global {
  interface Window {
    Razorpay: any;
  }
}

// Goshala Statistics Component for Home Page
const GoshalaStats: React.FC = () => {
  const districtStats = getDistrictStats();
  const topDistricts = Object.entries(districtStats)
    .sort(([,a], [,b]) => b.count - a.count)
    .slice(0, 6);

  const totalGoshalas = GOSHALA_DATA.length;
  const totalAnimals = GOSHALA_DATA.reduce((sum, g) => sum + g.animalCount, 0);


  return (
    <Section
      id="goshala-stats"
      title="Goshalas Across Karnataka"
      titleKannada="ಕರ್ನಾಟಕದಾದ್ಯಂತ ಗೋಶಾಲೆಗಳು"
      bgColor="bg-gradient-to-br from-yellow-50 to-red-50"
    >
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Summary Stats */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-red-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Overview</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-yellow-50 rounded-xl">
              <div>
                <div className="text-2xl font-bold text-red-600">{totalGoshalas}</div>
                <div className="text-gray-600">Total Goshalas</div>
              </div>
              <div className="text-3xl">🏠</div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-yellow-50 rounded-xl">
              <div>
                <div className="text-2xl font-bold text-red-600">{totalAnimals.toLocaleString()}</div>
                <div className="text-gray-600">Cows Protected</div>
              </div>
              <div className="text-3xl">🐄</div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-yellow-50 rounded-xl">
              <div>
                <div className="text-2xl font-bold text-red-600">{getUniqueDistricts().length}</div>
                <div className="text-gray-600">Districts Covered</div>
              </div>
              <div className="text-3xl">📍</div>
            </div>
          </div>
        </div>

        {/* Top Districts */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-red-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Top Districts</h3>
          <div className="space-y-3">
            {topDistricts.map(([district, stats], index) => (
              <div key={district} className="flex items-center justify-between p-3 hover:bg-red-50 rounded-xl transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-yellow-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{district}</div>
                    <div className="text-sm text-gray-600">{stats.totalAnimals} cows</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-red-600">{stats.count}</div>
                  <div className="text-xs text-gray-500">goshalas</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Goshalas */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-red-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Featured Goshalas</h3>
          <div className="space-y-4">
            {GOSHALA_DATA.slice(0, 4).map((goshala, index) => (
              <div key={goshala.slNo} className="p-4 border border-gray-200 rounded-xl hover:border-red-300 transition-colors">
                <div className="font-semibold text-gray-900 text-sm mb-1">
                  {goshala.goshalaName.length > 30 
                    ? goshala.goshalaName.substring(0, 30) + '...' 
                    : goshala.goshalaName}
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  {goshala.taluk}, {goshala.district}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                    {goshala.animalCount} cows
                  </span>
                  <button 
                    className="text-xs text-red-600 hover:text-red-800 font-medium"
                    onClick={() => {
                      // Scroll to services section for donation
                      const element = document.getElementById('services');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Support →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};
const PaymentGateway: React.FC<PaymentProps> = ({
  amount,
  donorName,
  email,
  phone,
  type,
  donationPurpose,
  selectedGoshala,  // ← Added this missing parameter

}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const RAZORPAY_KEY_ID = 'rzp_test_lXJEZJELQ8emOR';

  const loadRazorpay = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const convertNumberToWords = (num: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const thousands = ['', 'Thousand', 'Lakh', 'Crore'];


    if (num === 0) return 'Zero';

    const convertHundreds = (n: number): string => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        return result;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result;
    };

    let result = '';
    let place = 0;
    
    while (num > 0) {
      if (num % 1000 !== 0) {
        result = convertHundreds(num % 1000) + thousands[place] + ' ' + result;
      }
      num = Math.floor(num / 1000);
      place++;
    }

    return result.trim();
  };

  const generateCertificate = (paymentId: string) => {
    try {
      const goshalaDetails = selectedGoshala ?
         `<br><br><strong>Goshala:</strong> ${selectedGoshala.goshalaName}<br><strong>Location:</strong> ${selectedGoshala.taluk}, ${selectedGoshala.district}<br><strong>Animals:</strong> ${selectedGoshala.animalCount} cows`
        : '';
        
      const certificateHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>GOVU RAKSHATHI RAKSHITHA Certificate</title>
          <style>
            @page { size: A4; margin: 0; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
            
            body {
              font-family: 'Times New Roman', serif;
              margin: 0;
              padding: 0;
              width: 210mm;
              height: 297mm;
              background: #d4c79a;
              background-image: 
                radial-gradient(circle at 20% 20%, rgba(212, 175, 55, 0.3) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(212, 175, 55, 0.3) 0%, transparent 50%);
              position: relative;
            }
            
            .certificate-container {
              position: absolute;
              top: 30mm;
              left: 20mm;
              width: 170mm;
              height: 237mm;
              background: #f8f5e8;
              border: 4px solid #d4af37;
              border-radius: 15px;
              padding: 20mm;
              box-sizing: border-box;
              text-align: center;
            }
            
            .decorative-border {
              position: absolute;
              top: 8px; left: 8px; right: 8px; bottom: 8px;
              border: 2px dashed #d4af37;
              border-radius: 10px;
            }
            
            .om-symbol { color: #d4af37; font-size: 32px; margin-bottom: 10px; font-weight: bold; }
            .title { color: #8b4513; font-size: 24px; font-weight: bold; letter-spacing: 2px; margin-bottom: 8px; text-transform: uppercase; }
            .subtitle { color: #8b4513; font-size: 16px; font-style: italic; margin-bottom: 20px; }
            .blessing-quote { color: #d4af37; font-size: 12px; font-style: italic; margin-bottom: 20px; line-height: 1.4; }
            .presented-to { color: #333; font-size: 14px; margin-bottom: 8px; }
            .recipient-name { color: #000; font-size: 22px; font-weight: bold; margin-bottom: 20px; text-decoration: underline; text-decoration-color: #d4af37; }
            .recognition-text { color: #333; font-size: 12px; line-height: 1.5; margin-bottom: 15px; }
            .amount { color: #8b4513; font-weight: bold; font-size: 14px; }
            .purpose-text { color: #333; font-size: 12px; line-height: 1.5; margin: 15px 0; }
            .blessing { color: #8b4513; font-size: 12px; font-style: italic; margin: 15px 0; line-height: 1.4; }
            .date { color: #000; font-size: 12px; font-weight: bold; margin: 15px 0; }
            .signature-section { text-align: left; margin: 20px 0; font-size: 12px; color: #333; }
            .signature-line { display: inline-block; width: 120px; border-bottom: 1px solid #000; margin-left: 10px; height: 16px; }
            .organization-info { color: #8b4513; font-size: 14px; font-weight: bold; margin-top: 20px; text-decoration: underline; text-decoration-color: #d4af37; }
            .department-info { color: #666; font-size: 10px; margin-top: 8px; line-height: 1.3; }
            .payment-reference { color: #666; font-size: 9px; margin-top: 15px; font-family: 'Courier New', monospace; }
            
            .corner-decoration { position: absolute; width: 20px; height: 20px; border: 2px solid #d4af37; }
            .corner-tl { top: 15px; left: 15px; border-right: none; border-bottom: none; }
            .corner-tr { top: 15px; right: 15px; border-left: none; border-bottom: none; }
            .corner-bl { bottom: 15px; left: 15px; border-right: none; border-top: none; }
            .corner-br { bottom: 15px; right: 15px; border-left: none; border-top: none; }
          </style>
        </head>
        <body>
          <div class="certificate-container">
            <div class="decorative-border"></div>
            <div class="corner-decoration corner-tl"></div>
            <div class="corner-decoration corner-tr"></div>
            <div class="corner-decoration corner-bl"></div>
            <div class="corner-decoration corner-br"></div>
            
            <div class="om-symbol">ॐ</div>
            <h1 class="title">GOVU RAKSHATHI RAKSHITHA</h1>
            <h2 class="subtitle">Certificate of Thanksgiving</h2>
            
            <div class="blessing-quote">
              "गावो विश्वस्य मातरः"<br>
              <em>Cows are the mothers of the world</em>
            </div>
            
            <div class="presented-to">Presented to:</div>
            <div class="recipient-name">${donorName.toUpperCase()}</div>
            
            <div class="recognition-text">
              In Grateful Recognition of Your Generous Donation of<br>
              <span class="amount">₹${amount.toLocaleString('en-IN')}/- (Rupees ${convertNumberToWords(amount)} Only)</span>
            </div>
            
            <div class="purpose-text">
              Your kindness supports the care and protection of cows at<br>
              <strong>Punyakoti Dattu Yojana, Government of Karnataka</strong>
              ${goshalaDetails}
            </div>
            
            <div class="blessing">
              <em>May your compassion bring blessings and prosperity.</em><br>
              गो सेवा से मिले आपको सुख-समृद्धि
            </div>
            
            <div class="date">Date: ${new Date().toLocaleDateString('en-IN')}</div>
            
            <div class="signature-section">
              Seal/Signature:<span class="signature-line"></span>
            </div>
            
            <div class="organization-info">
              Punyakoti Dattu Yojana, Karnataka
            </div>
            
            <div class="department-info">
              Department of Animal Husbandry and Veterinary Services<br>
              Government of Karnataka
            </div>
            
            <div class="payment-reference">
              Payment Reference: ${paymentId}
            </div>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(certificateHTML);
        printWindow.document.close();
        
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => {
              printWindow.close();
            }, 1000);
          }, 500);
        };
        
        alert('Certificate will open in a new window. Please use "Save as PDF" in the print dialog.');
      } else {
        const blob = new Blob([certificateHTML], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `GOVU_RAKSHATHI_RAKSHITHA_Certificate_${donorName.replace(/\s+/g, '_')}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        alert('Certificate downloaded as HTML! Open the file and print to save as PDF.');
      }

    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Certificate generation failed, but payment was successful. Please contact support.');
    }
  };

  const validateFormData = (): boolean => {
    if (!donorName.trim()) {
      setError('Donor name is required');
      return false;
    }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError('Valid email is required');
      return false;
    }
    if (!phone.trim() || !/^\d{10}$/.test(phone.replace(/\D/g, ''))) {
      setError('Valid 10-digit phone number is required');
      return false;
    }
    if (amount <= 0) {
      setError('Invalid amount');
      return false;
    }
    return true;
  };

  const handlePayment = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!validateFormData()) {
        setIsLoading(false);
        return;
      }

      const isLoaded = await loadRazorpay();
      if (!isLoaded) {
        throw new Error('Failed to load Razorpay SDK. Please check your internet connection and try again.');
      }

      const paymentDescription = selectedGoshala 
        ? `${donationPurpose || 'Goshala Support'} - ${selectedGoshala.goshalaName}, ${selectedGoshala.district}`
        : donationPurpose || 'Goshala Support Donation';

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: amount * 100,
        currency: 'INR',
        name: 'Punyakoti Dattu Yojana',
        description: paymentDescription,
        
        handler: function (response: any) {
          setIsLoading(false);
          alert(`Payment successful! 
Payment ID: ${response.razorpay_payment_id}
Your GOVU RAKSHATHI RAKSHITHA certificate will be generated shortly.`);
          generateCertificate(response.razorpay_payment_id);
        },
        
        prefill: {
          name: donorName,
          email: email,
          contact: phone.replace(/\D/g, ''),
        },
        
        theme: {
          color: '#DC2626',
        },
        
        modal: {
          ondismiss: function() {
            setIsLoading(false);
            setError('Payment cancelled. You can try again anytime.');
          }
        },
        
        retry: {
          enabled: true,
          max_count: 3
        },
        
        timeout: 300,
        remember_customer: false,
        
        error: function(error: any) {
          setIsLoading(false);
          setError(`Payment failed: ${error.description || 'Unknown error occurred'}`);
        }
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();

    } catch (error: any) {
      setIsLoading(false);
      setError(error.message || 'Failed to initialize payment. Please try again.');
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="text-sm">{error}</p>
        </div>
      )}
      
      <motion.button
        className={`w-full bg-gradient-to-r from-red-600 to-yellow-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
          isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={handlePayment}
        disabled={isLoading}
        whileHover={!isLoading ? { scale: 1.05 } : {}}
        whileTap={!isLoading ? { scale: 0.95 } : {}}
      >
        {isLoading ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Processing...</span>
          </div>
        ) : (
          `Donate ₹${amount.toLocaleString('en-IN')} Now`
        )}
      </motion.button>
      
      <div className="text-xs text-gray-600 text-center space-y-1">
        <p>✓ Secure payment powered by Razorpay</p>
        <p>✓ GOVU RAKSHATHI RAKSHITHA certificate included</p>
        <p>✓ Multiple payment methods supported</p>
      </div>
    </div>
  );
};

// Header Component
const Header: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrollY > 50
          ? 'bg-white/95 backdrop-blur-lg shadow-xl border-b border-red-100'
          : 'bg-gradient-to-r from-red-600 to-yellow-500'
      }`}
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3">
            <motion.div 
              className="flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <img 
                src="/images/anihus.jpeg" 
                alt="Karnataka Government Logo"
                className="w-12 h-12 object-contain bg-white rounded-full p-1 shadow-lg border border-red-200"
              />
            </motion.div>
            <div>
              <h1 className={`text-lg font-bold transition-colors ${scrollY > 50 ? 'text-gray-900' : 'text-white'}`}>
                ಪುಣ್ಯಕೋಟಿ ದತ್ತು ಯೋಜನೆ
              </h1>
              <p className={`text-xs transition-colors ${scrollY > 50 ? 'text-gray-600' : 'text-white/80'}`}>
              DEPARTMENT OF ANIMAL HUSBANDRY AND VETERINARY SERVICES</p>
            </div>
          </div>
          <nav className="hidden md:flex space-x-6">
            {['Home', 'About', 'Services', 'Process', 'FAQ', 'Contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase())}
                className={`font-medium transition-colors hover:scale-105 transform text-sm ${
                  activeSection === item.toLowerCase()
                    ? scrollY > 50 ? 'text-red-500' : 'text-yellow-200'
                    : scrollY > 50 ? 'text-gray-700 hover:text-red-500' : 'text-white hover:text-yellow-200'
                }`}
              >
                {item}
              </button>
            ))}
          </nav>
          <div className="flex items-center space-x-3">
            <a
              rel="noopener noreferrer"
              className={`px-4 py-2 rounded-full font-semibold transition-all hover:scale-105 transform text-sm ${
                scrollY > 50 ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-white text-red-500 hover:bg-red-50'
              }`}
            >
              Official Portal
            </a>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

// Hero Component
const Hero: React.FC = () => {
  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.section
      id="home"
      className="relative pt-20 pb-16 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 via-yellow-500/20 to-red-600/20"></div>
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='lotus-pattern' x='0' y='0' width='40' height='40' patternUnits='userSpaceOnUse'%3E%3Cpath d='M20 10 Q15 15 20 20 Q25 15 20 10' fill='%23dc2626' opacity='0.3'/%3E%3Cpath d='M20 25 Q15 30 20 35 Q25 30 20 25' fill='%23eab308' opacity='0.3'/%3E%3Ccircle cx='20' cy='20' r='2' fill='%23dc2626' opacity='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='80' height='80' fill='url(%23lotus-pattern)'/%3E%3C/svg%3E")`
        }}
      />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-red-100 to-yellow-100 text-red-700 px-6 py-3 rounded-full text-sm font-medium border border-red-200">
              <Award className="w-4 h-4" />
              <span>GOVERNMENT OF KARNATAKA INITIATIVE</span>
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-yellow-500">
                ಪುಣ್ಯಕೋಟಿ
              </span>
              <br />
              Sacred Cow
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-red-600">
                {" "}Protection
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              Support Karnataka's cow protection program. Help preserve native breeds, support goshalas, and contribute to our cultural heritage.{" "}
              <span className="font-semibold text-red-600">215 Government Goshalas</span> await your support.
            </p>
            
           
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8">
              {[
                { number: "215", label: "Government Goshalas", icon: <Shield className="w-6 h-6" /> },
                { number: "10,000+", label: "Cows Protected", icon: <Heart className="w-6 h-6" /> },
                { number: "5,000+", label: "Donations", icon: <Users className="w-6 h-6" /> },
                { number: "24/7", label: "Care Support", icon: <Zap className="w-6 h-6" /> },
              ].map((stat, index) => (
                <div
                  key={index}
                  className="text-center space-y-2 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-red-100"
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-red-500 to-yellow-500 text-white rounded-full mx-auto">
                    {stat.icon}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{stat.number}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-500">
              <img
                src="/images/real_feed.png"
                alt="Indian Cow in Goshala"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 text-white">
                <h3 className="text-2xl font-bold">ಗೋಮಾತೆ | Gau Mata</h3>
                <p className="text-white/80">Sacred & Protected in Karnataka</p>
              </div>
            </div>
            
            <div className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-lg border-2 border-yellow-300">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">Daily</div>
                <div className="text-sm text-gray-600">Feeding</div>
              </div>
            </div>
            
            <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-red-500 to-yellow-500 text-white rounded-xl p-4 shadow-lg">
              <div className="text-center">
                <div className="text-2xl font-bold">Basic</div>
                <div className="text-sm opacity-90">Support</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

// Section Component
const Section: React.FC<SectionProps> = ({
  id,
  title,
  titleKannada,
  children,
  bgColor = 'bg-white',
}) => (
  <motion.section
    id={id}
    className={`${bgColor} py-16 relative`}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
    viewport={{ once: true }}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center space-y-4 mb-16">
        <h2 className="text-4xl font-bold text-gray-900">
          {titleKannada && <span className="block">{titleKannada}</span>}
          {title}
        </h2>
      </div>
      {children}
    </div>
  </motion.section>
);

// Card Component
const Card: React.FC<CardProps> = ({
  title,
  titleKannada,
  description,
  buttonText,
  onClick,
  image,
  icon,
  color = 'from-red-500 to-red-600',
}) => (
  <motion.div
    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100"
    whileHover={{ scale: 1.05 }}
    transition={{ duration: 0.3 }}
  >
  
  {image && (
      <div className="aspect-4/3 overflow-hidden relative">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
      </div>
    )}
    <div className="p-8 relative">
      {icon && (
        <div
          className={`w-16 h-16 bg-gradient-to-r ${color} rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform ${image ? '-mt-16' : ''} relative z-10 border-4 border-white`}
        >
          {icon}
        </div>
      )}
      <h3 className="text-2xl font-bold text-gray-900 mb-4">
        {titleKannada && <span className="block text-red-600 font-semibold mb-2">{titleKannada}</span>}
        {title}
      </h3>
      <p className="text-gray-600 mb-6 leading-relaxed">{description}</p>
      <button
        className={`bg-gradient-to-r ${color} text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all`}
        onClick={onClick}
      >
        {buttonText}
      </button>
    </div>
  </motion.div>
);

// Services Component
const Services: React.FC = () => {
  const [showPayment, setShowPayment] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    amount: 0,
    donorName: '',
    email: '',
    phone: '',
    type: 'donation' as 'donation',
    donationPurpose: '',
    selectedGoshala: undefined as GoshalaData | undefined,
  });

  const services: CardProps[] = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Support Cow Care',
      titleKannada: 'ಗೋವಿನ ಆರೈಕೆಗೆ ಬೆಂಬಲ',
      description: 'Donate to provide daily care, nutrition and medical support for rescued cows.',
      buttonText: 'Donate Now',
      color: 'from-red-500 to-red-600',
      image: '/images/ghosala.png',
      onClick: () => {
        setPaymentDetails({
          amount: 2100,
          donorName: '',
          email: '',
          phone: '',
          type: 'donation',
          donationPurpose: 'Monthly Cow Care Support',
          selectedGoshala: undefined,
        });
        setShowPayment(true);
      },
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Feed a Cow',
      titleKannada: 'ಗೋವಿಗೆ ಆಹಾರ ನೀಡಿ',
      description: 'Sponsor daily meals for cows in goshalas across Karnataka.',
      buttonText: 'Donate Now',
      color: 'from-yellow-500 to-yellow-600',
      image: '/images/ghosala.png',
      onClick: () => {
        setPaymentDetails({
          amount: 70,
          donorName: '',
          email: '',
          phone: '',
          type: 'donation',
          donationPurpose: 'Daily Cow Feeding',
          selectedGoshala: undefined,
        });
        setShowPayment(true);
      },
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: 'Support Goshalas',
      titleKannada: 'ಗೋಶಾಲೆಗಳಿಗೆ ದೇಣಿಗೆ ನೀಡಿ',
      description: 'Support infrastructure and medical care for cow shelters.',
      buttonText: 'Donate Now',
      color: 'from-red-600 to-yellow-500',
      image: '/images/ghosala.png',
      onClick: () => {
        setPaymentDetails({
          amount: 100,
          donorName: '',
          email: '',
          phone: '',
          type: 'donation',
          donationPurpose: 'Goshala Infrastructure',
          selectedGoshala: undefined,
        });
        setShowPayment(true);
      },
    },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentDetails({ ...paymentDetails, [e.target.name]: e.target.value });
  };

  return (
    <Section
      id="services"
      title="How You Can Help"
      titleKannada="ನಿಮ್ಮ ಸಹಾಯದ ಮಾರ್ಗಗಳು"
      bgColor="bg-gradient-to-br from-red-50 to-yellow-50"
    >
      <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center mb-12">
        Choose from three meaningful ways to support cow welfare and make a lasting impact.
      </p>
      <div className="grid md:grid-cols-3 gap-8">
        {services.map((service, index) => (
          <Card
            key={index}
            title={service.title}
            titleKannada={service.titleKannada}
            description={service.description}
            buttonText={service.buttonText}
            image={service.image}
            icon={service.icon}
            color={service.color}
            onClick={service.onClick}
          />
        ))}
      </div>
      {showPayment && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4 relative">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl font-bold"
              onClick={() => setShowPayment(false)}
            >
              ✕
            </button>
            <h3 className="text-2xl font-bold mb-4">Make a Donation</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-900 font-semibold mb-2">Name</label>
                <input
                  type="text"
                  name="donorName"
                  value={paymentDetails.donorName}
                  onChange={handleInputChange}
                  className="w-full p-3 border-2 border-red-500 rounded-lg"
                  placeholder="Your Name"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-900 font-semibold mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={paymentDetails.email}
                  onChange={handleInputChange}
                  className="w-full p-3 border-2 border-red-500 rounded-lg"
                  placeholder="Your Email"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-900 font-semibold mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={paymentDetails.phone}
                  onChange={handleInputChange}
                  className="w-full p-3 border-2 border-red-500 rounded-lg"
                  placeholder="Your Phone Number"
                  required
                />
              </div>
              <p className="text-gray-900 font-semibold">
                Amount: ₹{paymentDetails.amount.toLocaleString()}
              </p>
              <div className="flex justify-between gap-4">
                <button
                  className="bg-gray-500 text-white px-6 py-2 rounded-full hover:bg-gray-600 transition-colors"
                  onClick={() => setShowPayment(false)}
                >
                  Cancel
                </button>
                <PaymentGateway {...paymentDetails} />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </Section>
  );
};

// Donation Calculator Component
const DonationCalculator: React.FC = () => {
  const [selectedDonationAmount, setSelectedDonationAmount] = useState('100');
  const [customAmount, setCustomAmount] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    amount: 100,
    donorName: '',
    email: '',
    phone: '',
    type: 'donation' as 'donation',
    donationPurpose: 'General Support',
    selectedGoshala: undefined as GoshalaData | undefined,
  });

  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [availableGoshalas, setAvailableGoshalas] = useState<GoshalaData[]>([]);

  const handleDistrictChange = (district: string) => {
    setSelectedDistrict(district);
    const goshalas = getGoshalasByDistrict(district);
    setAvailableGoshalas(goshalas);
    setPaymentDetails(prev => ({ ...prev, selectedGoshala: undefined }));
  };

  const handleGoshalaChange = (goshala: GoshalaData) => {
    setPaymentDetails(prev => ({ ...prev, selectedGoshala: goshala }));
  };

  const donationAmounts = [
    { amount: 70, label: '1 Day Feeding', description: 'Feed one cow for a day', impact: '1 cow fed for 1 day' },
    { amount: 100, label: 'Basic Support', description: 'Basic daily cow care', impact: '1 cow basic care' },
    { amount: 500, label: 'Weekly Support', description: 'Support cow care for a week', impact: '1 cow cared for 1 week' },
    { amount: 2100, label: 'Monthly Care', description: 'Monthly cow care package', impact: '1 cow cared for 1 month' },
    { amount: 5000, label: 'Medical Fund', description: 'Emergency medical support', impact: 'Medical care for 5 cows' },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentDetails({ ...paymentDetails, [e.target.name]: e.target.value });
  };

  const handleProceed = () => {
    let amount = parseInt(selectedDonationAmount);
    if (selectedDonationAmount === 'custom' && customAmount) {
      amount = parseInt(customAmount);
    }
    
    const donation = donationAmounts.find((d) => d.amount === amount);
    setPaymentDetails({
      ...paymentDetails,
      amount: amount,
      donationPurpose: donation ? donation.description : 'Custom Donation',
      selectedGoshala: undefined,
    });
    setShowPayment(true);
  };

  return (
    <Section
      id="calculator"
      title="Donation Calculator"
      titleKannada="ದಾನ ಲೆಕ್ಕಾಚಾರ"
      bgColor="bg-white"
    >
      <p className="text-xl text-gray-600 text-center mb-12">
        Choose your donation amount and see the impact you can make.
      </p>
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Choose Donation Amount</h3>
            <div className="space-y-3">
              {donationAmounts.map((donation) => (
                <label
                  key={donation.amount}
                  className={`flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all hover:bg-red-50 ${
                    selectedDonationAmount === donation.amount.toString()
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-red-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="amount"
                    value={donation.amount}
                    checked={selectedDonationAmount === donation.amount.toString()}
                    onChange={(e) => setSelectedDonationAmount(e.target.value)}
                    className="w-5 h-5 text-red-500"
                  />
                  <div className="flex-1 ml-4">
                    <div className="font-semibold text-gray-900">{donation.label}</div>
                    <div className="text-gray-600 text-sm">{donation.description}</div>
                    <div className="text-red-600 text-xs font-medium">Impact: {donation.impact}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-red-600">
                      ₹{donation.amount.toLocaleString()}
                    </div>
                  </div>
                </label>
              ))}
              <label
                className={`flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all hover:bg-red-50 ${
                  selectedDonationAmount === 'custom'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-red-300'
                }`}
              >
                <input
                  type="radio"
                  name="amount"
                  value="custom"
                  checked={selectedDonationAmount === 'custom'}
                  onChange={(e) => setSelectedDonationAmount(e.target.value)}
                  className="w-5 h-5 text-red-500"
                />
                <div className="flex-1 ml-4">
                  <div className="font-semibold text-gray-900">Custom Amount</div>
                  <div className="text-gray-600 text-sm mb-2">Enter your preferred amount (min ₹10)</div>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full p-2 border border-gray-300 rounded focus:border-red-500 focus:outline-none"
                    min="10"
                  />
                </div>
              </label>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-red-600 to-yellow-500 rounded-2xl p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle cx="50" cy="30" r="20" fill="currentColor" />
                <ellipse cx="50" cy="70" rx="25" ry="15" fill="currentColor" />
              </svg>
            </div>
            <h4 className="text-2xl font-bold mb-6 relative z-10">Your Donation Impact</h4>
            <div className="space-y-4 relative z-10">
              {[
                { icon: '🌾', text: 'Daily nutritious fodder and fresh water' },
                { icon: '🏥', text: 'Regular health check-ups and vaccinations' },
                { icon: '💊', text: 'Medical treatment when needed' },
                { icon: '🏠', text: 'Safe and clean shelter facilities' },
                { icon: '🤝', text: 'Support for native breed preservation' },
                { icon: '📜', text: 'GOVU RAKSHATHI RAKSHITHA certificate' },
              ].map((impact, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <span className="text-xl">{impact.icon}</span>
                  <span>{impact.text}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-8 p-4 bg-white/20 rounded-lg backdrop-blur-sm">
              <h5 className="font-bold mb-2">🎯 Current Goal</h5>
              <p className="text-sm">Help us raise ₹10,00,000 this month to support 500 cows across Karnataka goshalas.</p>
              <div className="mt-3 bg-white/30 rounded-full h-2">
                <div className="bg-white rounded-full h-2 w-3/4"></div>
              </div>
              <p className="text-xs mt-1">75% funded - ₹7,50,000 raised</p>
            </div>
            
            <motion.button
              className="w-full bg-white text-red-600 py-4 rounded-xl font-bold mt-6 hover:bg-red-50 transition-colors relative z-10 text-lg"
              onClick={handleProceed}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Proceed to Donate | ದಾನ ಮಾಡಿ
            </motion.button>
          </div>
        </div>
      </div>
      
      {showPayment && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4 relative">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl font-bold"
              onClick={() => setShowPayment(false)}
            >
              ✕
            </button>
            <h3 className="text-2xl font-bold mb-4">Complete Your Donation</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-900 font-semibold mb-2">Name</label>
                <input
                  type="text"
                  name="donorName"
                  value={paymentDetails.donorName}
                  onChange={handleInputChange}
                  className="w-full p-3 border-2 border-red-500 rounded-lg"
                  placeholder="Your Name"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-900 font-semibold mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={paymentDetails.email}
                  onChange={handleInputChange}
                  className="w-full p-3 border-2 border-red-500 rounded-lg"
                  placeholder="Your Email"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-900 font-semibold mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={paymentDetails.phone}
                  onChange={handleInputChange}
                  className="w-full p-3 border-2 border-red-500 rounded-lg"
                  placeholder="Your Phone Number"
                  required
                />
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-gray-900 font-semibold">
                  Amount: ₹{paymentDetails.amount.toLocaleString()}
                </p>
                <p className="text-gray-600 text-sm">
                  Purpose: {paymentDetails.donationPurpose}
                </p>
                {paymentDetails.selectedGoshala && (
                  <div className="mt-2 pt-2 border-t border-red-200">
                    <p className="text-gray-900 font-medium text-sm">Selected Goshala:</p>
                    <p className="text-gray-700 text-sm">{paymentDetails.selectedGoshala.goshalaName}</p>
                    <p className="text-gray-600 text-xs">{paymentDetails.selectedGoshala.taluk}, {paymentDetails.selectedGoshala.district}</p>
                    <p className="text-gray-600 text-xs">{paymentDetails.selectedGoshala.animalCount} cows</p>
                  </div>
                )}
              </div>
              
              {/* Goshala Selection */}
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-900 font-semibold mb-2">Select District (Optional)</label>
                  <select
                    value={selectedDistrict}
                    onChange={(e) => handleDistrictChange(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                  >
                    <option value="">Any District (General Fund)</option>
                    {getUniqueDistricts().map((district) => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>
                
                {availableGoshalas.length > 0 && (
                  <div>
                    <label className="block text-gray-900 font-semibold mb-2">Select Specific Goshala (Optional)</label>
                    <select
                      value={paymentDetails.selectedGoshala?.slNo || ''}
                      onChange={(e) => {
                        const goshala = availableGoshalas.find(g => g.slNo === parseInt(e.target.value));
                        if (goshala) handleGoshalaChange(goshala);
                      }}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                    >
                      <option value="">District General Fund</option>
                      {availableGoshalas.map((goshala) => (
                        <option key={goshala.slNo} value={goshala.slNo}>
                          {goshala.goshalaName.length > 50 
                            ? goshala.goshalaName.substring(0, 50) + '...' 
                            : goshala.goshalaName} ({goshala.animalCount} cows)
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="flex justify-between gap-4">
                <button
                  className="bg-gray-500 text-white px-6 py-2 rounded-full hover:bg-gray-600 transition-colors"
                  onClick={() => setShowPayment(false)}
                >
                  Cancel
                </button>
                <PaymentGateway {...paymentDetails} />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </Section>
  );
};

// Contact Form Component
const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
    district: '',
    subscription: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Thank you for your message! Well respond soon.');
    setFormData({ name: '', email: '', phone: '', message: '', district: '', subscription: false });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: (e.target as HTMLInputElement).checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const districts = [
    'Bengaluru', 'Mysore', 'Mangalore', 'Hubli', 'Belagavi', 'Tumkur', 'Shimoga', 'Bidar',
    'Davangere', 'Hassan', 'Udupi', 'Chikmagalur', 'Kolar', 'Mandya', 'Raichur', 'Gulbarga',
  ];

  return (
    <Section id="contact" title="Get in Touch" titleKannada="ಸಂಪರ್ಕದಲ್ಲಿರಿ" bgColor="bg-white">
      <motion.form
        className="max-w-lg mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        onSubmit={handleSubmit}
      >
        <div>
          <label htmlFor="name" className="block text-gray-900 font-semibold mb-2">Full Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full p-3 border-2 border-red-500 rounded-lg focus:outline-none focus:border-red-600"
            placeholder="Your Name"
            required
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-gray-900 font-semibold mb-2">Email Address</label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 pl-10 border-2 border-red-500 rounded-lg focus:outline-none focus:border-red-600"
              placeholder="Your Email"
              required
            />
          </div>
        </div>
        <div>
          <label htmlFor="phone" className="block text-gray-900 font-semibold mb-2">Phone Number</label>
          <div className="relative">
            <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full p-3 pl-10 border-2 border-red-500 rounded-lg focus:outline-none focus:border-red-600"
              placeholder="Your Phone Number"
            />
          </div>
        </div>
        <div>
          <label htmlFor="district" className="block text-gray-900 font-semibold mb-2">District</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <select
              id="district"
              name="district"
              value={formData.district}
              onChange={handleChange}
              className="w-full p-3 pl-10 border-2 border-red-500 rounded-lg focus:outline-none focus:border-red-600"
              required
            >
              <option value="">Select a District</option>
              {districts.map((district, index) => (
                <option key={index} value={district}>{district}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="message" className="block text-gray-900 font-semibold mb-2">Message</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="w-full p-3 border-2 border-red-500 rounded-lg focus:outline-none focus:border-red-600"
            rows={5}
            placeholder="Your Message"
            required
          />
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="subscription"
            name="subscription"
            checked={formData.subscription}
            onChange={handleChange}
            className="h-4 w-4 text-red-500 border-red-500 rounded focus:ring-red-500"
          />
          <label htmlFor="subscription" className="ml-2 text-gray-900">
            Subscribe to updates about Punyakoti Dattu Yojana
          </label>
        </div>
        <motion.button
          type="submit"
          className="bg-gradient-to-r from-red-600 to-yellow-500 text-white py-3 px-6 rounded-xl font-bold hover:shadow-xl transition-all w-full"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Send Message | ಸಂದೇಶ ಕಳುಹಿಸಿ
        </motion.button>
      </motion.form>
    </Section>
  );
};

// Footer Component
const Footer: React.FC = () => (
  <motion.footer
    className="bg-gradient-to-r from-red-600 to-yellow-500 text-white py-12"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1 }}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center space-x-3 mb-4">
            <img 
              src="/images/anihus.jpeg" 
              alt="Karnataka Government Logo"
              className="w-12 h-12 object-contain bg-white rounded-full p-1"
            />
            <div>
              <h3 className="text-xl font-bold">Punyakoti Dattu Yojana</h3>
              <p className="text-white/80 text-sm">Government of Karnataka</p>
            </div>
          </div>
          <p className="text-white/80 mb-4">
            A Karnataka Government initiative to protect cows and sustain goshalas through community support.
          </p>
        </div>
        <div>
          <h3 className="text-xl font-bold mb-4">Quick Links</h3>
          <ul className="space-y-2">
            {['Home', 'About', 'Services', 'Process', 'FAQ', 'Contact'].map((item) => (
              <li key={item}>
                <a 
                  href={`#${item.toLowerCase()}`} 
                  className="hover:text-yellow-200 text-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById(item.toLowerCase());
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  {item}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-xl font-bold mb-4">Donation Options</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex justify-between">
              <span>Daily Feeding</span>
              <span className="font-semibold">₹70</span>
            </li>
            <li className="flex justify-between">
              <span>Basic Support</span>
              <span className="font-semibold">₹100</span>
            </li>
            <li className="flex justify-between">
              <span>Weekly Care</span>
              <span className="font-semibold">₹500</span>
            </li>
            <li className="flex justify-between">
              <span>Monthly Care</span>
              <span className="font-semibold">₹2,100</span>
            </li>
          </ul>
          <p className="text-white/80 text-xs mt-3">
            ✓ GOVU RAKSHATHI RAKSHITHA certificate included
          </p>
        </div>
        <div>
          <h3 className="text-xl font-bold mb-4">Contact & Support</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>support@punyakoti.kar.gov.in</span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone className="w-4 h-4" />
              <span>+91 80-2235-2828</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4" />
              <span>Bengaluru, Karnataka</span>
            </div>
          </div>
          <div className="mt-4">
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 hover:text-yellow-200 text-sm bg-white/10 px-3 py-2 rounded-lg"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Official Portal</span>
            </a>
          </div>
        </div>
      </div>
      <div className="mt-8 pt-8 border-t border-white/20">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-center md:text-left">
            <p className="text-white/80 text-sm">
              © 2025 Punyakoti Dattu Yojana, Government of Karnataka. All rights reserved.
            </p>
            <p className="text-white/60 text-xs mt-1">
              Department of Animal Husbandry and Veterinary Services
            </p>
          </div>
          <div className="flex space-x-6 text-sm">
            <a href="#" className="hover:text-yellow-200">Privacy Policy</a>
            <span className="text-white/40">|</span>
            <a href="#" className="hover:text-yellow-200">Terms of Service</a>
            <span className="text-white/40">|</span>
            <a href="#" className="hover:text-yellow-200">Accessibility</a>
          </div>
        </div>
      </div>
    </div>
  </motion.footer>
);

// Live Chat Widget Component
const LiveChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <motion.button
        className="bg-gradient-to-r from-red-500 to-yellow-500 text-white p-4 rounded-full shadow-lg hover:shadow-xl"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(!isOpen)}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 500 }}
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3 3h10v2H5V8zm0 4h6v2H5v-2z" />
        </svg>
      </motion.button>
      {isOpen && (
        <motion.div
          className="bg-white text-gray-900 p-6 rounded-lg mt-2 w-80 shadow-xl border border-red-100"
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center space-x-2 mb-3">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <h3 className="text-lg font-bold">Goshala Support</h3>
          </div>
          <p className="text-gray-600 mb-4 text-sm">
            Need help with donations or have questions about cow care? Our support team is here to assist you!
          </p>
          <div className="space-y-2">
            <button
              className="w-full bg-gradient-to-r from-red-500 to-yellow-500 text-white py-2 px-4 rounded text-sm font-semibold"
              onClick={() => {
                alert('Connecting to donation support...');
                setIsOpen(false);
              }}
            >
              💬 Start Chat
            </button>
            <button
              className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded text-sm"
              onClick={() => window.open('tel:+918022352828')}
            >
              📞 Call Support
            </button>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            Available: Mon-Sat, 9:00 AM - 6:00 PM
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Main App Component
const App: React.FC = () => (
  <div className="min-h-screen relative">
    {/* Background Image */}
    <div 
      className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-5 z-0"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='cow-pattern' x='0' y='0' width='50' height='50' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='25' cy='15' r='8' fill='%23dc2626' opacity='0.1'/%3E%3Cellipse cx='25' cy='35' rx='12' ry='8' fill='%23eab308' opacity='0.1'/%3E%3Cpath d='M15 10 Q20 5 25 10 Q30 5 35 10' stroke='%23dc2626' stroke-width='1' fill='none' opacity='0.1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23cow-pattern)'/%3E%3C/svg%3E")`
      }}
    />
    
    {/* Gradient Overlay */}
    <div className="fixed inset-0 bg-gradient-to-br from-red-50/80 via-white/90 to-yellow-50/80 z-0" />
    
    {/* Content */}
    <div className="relative z-10">
      <Header />
      <Hero />
      <GoshalaDirectory />
      <Services />
      <DonationCalculator />
      <Process />
      <Testimonials />
      <FAQ />
      <Section
        id="about"
        title="About Punyakoti Dattu Yojana"
        titleKannada="ಪುಣ್ಯಕೋಟಿ ದತ್ತು ಯೋಜನೆ ಬಗ್ಗೆ"
        bgColor="bg-gradient-to-br from-yellow-50 to-red-50"
      >
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <p className="text-lg text-gray-600 leading-relaxed">
              The Punyakoti Dattu Yojana is a pioneering initiative by the Government of Karnataka, 
              launched to protect and preserve our sacred cows and support goshalas across the state.
            </p>
            <div className="space-y-4">
              {[
                "Launched on July 28, 2022 by the Karnataka Government",
                "Supports 243 government-registered goshalas",
                "Protects over 15,000+ cows across Karnataka",
                "Covers all 31 districts of Karnataka",
                "Preserves native Indian cow breeds",
                "Provides medical care and nutrition to rescued cows",
                "Promotes sustainable agriculture through organic farming"
              ].map((point, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{point}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img
              src="/images/donate.png"
              alt="Goshala with cows"
              className="w-full rounded-2xl shadow-xl"
            />
          </div>
        </div>
      </Section>
      <ContactForm />
      <Footer />
      <LiveChatWidget />
    </div>
  </div>
);

export default App;
      